import { useState } from "react";
import { Button, Form, Input, Popover, Tooltip } from "antd";
import { DownOutlined, FilterOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { t } from "i18next";
import moment from "moment";
import DatePicker from "../DatePicker";
import Helper from "../../Helper";
import "./quick-filter.css";

/*
 * Grid üstü filtre çubuğunun ortak parçaları (randevu listesi, kasa). RIS hızlı filtre çubuğunun düzeni.
 * Parçalar antd Form'a bağlıdır: değerleri form alanlarına yazar ve formu gönderir; formun onFinish'i
 * sayfanın kendi filtre dönüşümünü yapar. Stiller: quick-filter.css (bu dosya yükler).
 */

const { RangePicker } = DatePicker;

export const DEFAULT_DATE_PRESETS = [
    { key: 'today', label: 'Today', option: Helper.Date.DATE_OPTIONS.TODAY },
    { key: 'yesterday', label: 'Yesterday', option: Helper.Date.DATE_OPTIONS.YESTERDAY },
];

/** Alan formda kayıtlı kalsın (onFinish'e girsin) ama görünen kontrol başka bir yerde olsun. */
export const FieldHolder = () => null;

const sameDay = (a, b) => !!a && !!b && moment(a).isSame(moment(b), 'day');

/**
 * Tarih kartı: [children] [Bugün | Dün] ‹ aralık ›. Değerler startKey/endKey form alanlarına yazılır ve form
 * hemen gönderilir. children: aralığın solunda karta eklenecek ek kontrol (ör. hakediş tarih türü).
 * Form alanları (FieldHolder) sayfada ayrıca kayıtlı olmalı.
 */
export function QuickDateRange({ form, disabled, presets = DEFAULT_DATE_PRESETS, startKey = 'startDate', endKey = 'endDate', label, children }) {
    const startDate = Form.useWatch(startKey, form)
    const endDate = Form.useWatch(endKey, form)
    // Dokunmatik ekranda tarih kutusu yazılamaz: dokununca klavye açılıp takvimin üstünü kapatıyor.
    const isTouch = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches

    const applyDates = (start, end) => {
        form.setFieldsValue({ [startKey]: start, [endKey]: end })
        form.submit()
    }

    const activePreset = presets.find(p => {
        const d = Helper.Date.getDateFromNow(p.option)
        return sameDay(startDate, d.start) && sameDay(endDate, d.end)
    })?.key

    // Aralığı kendi uzunluğu kadar kaydırır: tek gün → ±1 gün, 7 günlük aralık → önceki/sonraki 7 gün.
    // İleri giderken bitiş bugünü geçmez.
    const shiftDates = (direction) => {
        const begin = moment(startDate)
        const end = moment(endDate)
        const span = end.clone().startOf('day').diff(begin.clone().startOf('day'), 'days') + 1
        applyDates(
            begin.clone().add(direction * span, 'days'),
            moment.min(end.clone().add(direction * span, 'days'), moment())
        )
    }
    const canGoNext = !!endDate && moment(endDate).isBefore(moment(), 'day')

    return (
        <div className={`qf-group${disabled ? ' is-disabled' : ''}`} role="group" aria-label={label}>
            {children}
            <div className="qf-seg">
                {presets.map(p => (
                    <button
                        key={p.key}
                        type="button"
                        disabled={disabled}
                        aria-pressed={activePreset === p.key}
                        onClick={() => {
                            const d = Helper.Date.getDateFromNow(p.option)
                            applyDates(d.start.clone(), d.end.clone())
                        }}
                    >
                        {t(p.label)}
                    </button>
                ))}
            </div>
            <div className="qf-stepper">
                <Tooltip title={t('Previous')}>
                    <Button
                        size="small"
                        icon={<LeftOutlined />}
                        disabled={disabled || !startDate || !endDate}
                        onClick={() => shiftDates(-1)}
                    />
                </Tooltip>
                <RangePicker
                    size="small"
                    allowClear={false}
                    inputReadOnly={isTouch}
                    format="DD.MM.YY"
                    disabled={disabled}
                    locale={Helper.Date.getLocale()}
                    value={[startDate ? moment(startDate) : null, endDate ? moment(endDate) : null]}
                    onChange={(range) => {
                        if (range?.[0] && range?.[1])
                            applyDates(range[0], range[1])
                    }}
                />
                <Tooltip title={t('Next')}>
                    <Button
                        size="small"
                        icon={<RightOutlined />}
                        disabled={disabled || !startDate || !canGoNext}
                        onClick={() => shiftDates(1)}
                    />
                </Tooltip>
            </div>
        </div>
    )
}

/**
 * Arama kartı: dolu iken amber. Enter formu zaten gönderir (native submit), büyüteç ve çarpı burada gönderir.
 */
export function QuickSearchBox({ form, name = 'searchText', hint }) {
    const value = Form.useWatch(name, form) || ''

    return (
        <Tooltip title={hint} mouseEnterDelay={0.6} placement="bottomLeft">
            <div className={`qf-search${value.trim() ? ' is-active' : ''}`}>
                <Form.Item name={name} noStyle>
                    <Input.Search
                        size="small"
                        allowClear
                        placeholder={t('Type anything: name, ID, number, note...')}
                        onSearch={(v, e) => {
                            if (e?.type !== 'keydown') form.submit()
                        }}
                    />
                </Form.Item>
            </div>
        </Tooltip>
    )
}

const isFieldActive = (field, filter) => {
    const v = filter?.[field.name]
    if (v === undefined) return false
    if ('defaultValue' in field) return v !== field.defaultValue
    if (Array.isArray(v)) return v.length > 0
    return v !== null && `${v}`.trim() !== ''
}

/**
 * "Diğer filtreler" kartı: kesik çizgili düğme + iki sütunlu panel (Temizle / Uygula).
 * fields: [{ name, label (i18n), render: () => kontrol, visible?, defaultValue? }]
 * appliedFilter: uygulanmış filtre (queryModel.filter) — sayaç ve amber etiketler buna bakar.
 * Panel baştan çizilir ve kapanınca yok edilmez: alanlar formda kayıtlı kalsın ("Ara", URL'den gelen değerler).
 */
export function MoreFilters({ form, fields, appliedFilter }) {
    const [open, setOpen] = useState(false)
    const visibleFields = fields.filter(f => f.visible !== false)
    const activeCount = visibleFields.filter(f => isFieldActive(f, appliedFilter)).length

    const apply = () => {
        setOpen(false)
        form.submit()
    }

    const clear = () => {
        const values = {}
        for (const f of fields) values[f.name] = 'defaultValue' in f ? f.defaultValue : undefined
        form.setFieldsValue(values)
        apply()
    }

    return (
        <div className="qf-group">
            <Popover
                trigger="click"
                placement="bottomLeft"
                open={open}
                onOpenChange={setOpen}
                forceRender
                content={(
                    <div className="qf-panel">
                        <div className="qf-panel-grid">
                            {visibleFields.map(f => (
                                <div key={f.name} className={`qf-panel-field${isFieldActive(f, appliedFilter) ? ' is-active' : ''}`}>
                                    <span className="qf-panel-label">{t(f.label)}</span>
                                    <Form.Item name={f.name} noStyle>
                                        {f.render()}
                                    </Form.Item>
                                </div>
                            ))}
                        </div>
                        <div className="qf-panel-actions">
                            <Button size="small" onClick={clear}>{t('Clear')}</Button>
                            <Button size="small" type="primary" onClick={apply}>{t('Apply')}</Button>
                        </div>
                    </div>
                )}
            >
                <button
                    type="button"
                    className="qf-chip qf-chip-more"
                    aria-pressed={activeCount > 0}
                    aria-expanded={open}
                >
                    <FilterOutlined aria-hidden="true" />
                    {t('Other filters')}
                    {activeCount > 0 && <span className="qf-count">{activeCount}</span>}
                    <DownOutlined aria-hidden="true" />
                </button>
            </Popover>
        </div>
    )
}

/**
 * Formsuz arama kartı (istemci tarafında süzülen tanım listeleri): yazdıkça onChange çağrılır.
 */
export function SearchCard({ value, onChange, placeholder, hint }) {
    return (
        <Tooltip title={hint} mouseEnterDelay={0.6} placement="bottomLeft">
            <div className={`qf-search${`${value ?? ''}`.trim() ? ' is-active' : ''}`}>
                <Input.Search
                    size="small"
                    allowClear
                    value={value}
                    placeholder={placeholder ?? t('Search')}
                    onChange={(e) => onChange(e.target.value)}
                    onSearch={(v) => onChange(v)}
                />
            </div>
        </Tooltip>
    )
}

/** Tek filtre kartı (etiketsiz; alan adı placeholder'da). wide: birden çok alanı yan yana taşıyan kart. */
export function FilterCard({ children, wide, label }) {
    return (
        <div className={`qf-group appt-filter__field${wide ? ' appt-filter__field--group' : ''}`} role="group" aria-label={label}>
            {children}
        </div>
    )
}

/** Filtre satırlarının altındaki eylem düğmeleri (Yeni, Yenile, Excel …). */
export function FilterActions({ children }) {
    return <div className="qf-actions">{children}</div>
}
